import React, { useEffect } from "react";
import * as R from "ramda";
import { Content } from "@worcket/common/components/layout";
import { connect } from "react-redux";
import { compose } from "recompose";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import { dispatchOnMount, spinWhile } from "@worcket/common/hocs";
import { HorizontalBar, Doughnut, Bar, Chart } from "react-chartjs-2";
import { Icon, Tooltip } from "antd";

import { isLoading } from "../../utils/actions";
import * as gtag from "../../lib/gtag";
import Page from "../../components/display/Page";
import {
  fetchAnalyticsCandidatesOpps,
  fetchAnalyticsOpportunities,
  fetchAnalyticsCandidatesProfessions,
  fetchAnalyticsAverageTime,
  FETCH_ANALYTICS_CANDIDATES_OPPS,
  FETCH_ANALYTICS_OPPORTUNITIES,
  FETCH_ANALYTICS_CANDIDATES_PROFESSIONS,
  FETCH_ANALYTICS_AVERAGE_TIME
} from "../../store/actions/analytics";
import {
  exportCompanyCandidates,
  EXPORT_COMPANY_CANDIDATES
} from "../../store/actions/company";
import {
  exportOpportunityCandidates,
  EXPORT_OPPORTUNITY_CANDIDATES
} from "../../store/actions/opportunity";
import {
  exportTeamCandidates,
  EXPORT_TEAM_CANDIDATES
} from "../../store/actions/team";

import Button from "./../../components/Button";
import SkeletonAnalytics from "./SkeletonAnalytics";
import AnalyticsCard from "./AnalyticsCard";

import styled from "styled-components/macro";

const getNumberAndSuffix = (days, t) =>
  R.cond([
    [
      R.lt(1),
      R.always({
        number: days.toFixed(1),
        suffix: t("analytics:analyticsAverageTime.days")
      })
    ],
    [
      R.equals(1),
      R.always({
        number: days.toFixed(1),
        suffix: t("analytics:analyticsAverageTime.day")
      })
    ],
    [
      R.T,
      R.always({
        number: Math.round(days * 24),
        suffix: t("analytics:analyticsAverageTime.hours")
      })
    ]
  ])(days);

const AnalyticsContainer = styled.section`
  color: ${({ theme }) => theme["@primary"]};
`;

const CountContainer = styled.section`
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  justify-content: space-between;
  @media print {
    flex-basis: 100%;
    max-width: none !important;
    margin-top: 50px;
  }
`;

const ChartContainer = styled.section`
  max-width: 500px;
  flex: 1 1 500px;
  @media print {
    max-width: none;
    flex-basis: 100%;
    display: flex;
    justify-content: center;
  }
`;

const GridContainer = styled.section`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 10px;
  padding-left: 20px;
  padding-bottom: 20px;
  > article {
    margin: 0 auto;
  }
`;

const ListContainer = styled.ul`
  display: flex;
  padding: 0 20px;
  overflow-y: auto;
  max-height: 600px;
  max-width: 350px;
  @media print {
    page-break-inside: avoid;
    max-width: none;
    flex-basis: 100%;
  }
  flex-direction: row;
  flex-wrap: wrap;
  align-items: stretch;
  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }

  &::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }
`;

const ProfessionsContainer = styled.section`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  @media print {
    page-break-before: always;
    page-break-after: always;
  }
  > * {
    flex: 1 1 50%;
  }
  > canvas {
    max-width: calc(100% - 350px);
    @media print {
      max-width: 100%;
    }
  }
`;

const CandidatesContainer = styled.section`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
`;

const AverageTimeContainer = styled.section`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  flex-direction: row-reverse;
  > canvas {
    max-width: 500px;
  }
`;

const OpportunitiesContainer = styled.section`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  flex-direction: row-reverse;
  ${ChartContainer} {
    margin: 0 40px;
    max-width: 450px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-block: 1em;
  margin-inline: 16px;
`;

const AnalyticsTitle = styled.h1`
  font-size: 2em;
  margin: 0;

  @media print {
    display: none;
  }
`;

const AnalyticsSubtitle = styled.h2`
  font-size: 1.5em;
  margin: 0;

  @media print {
    display: none;
  }
`;

const PrintButton = styled.button`
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2em;
  color: white;
  box-shadow: 0px -2px 5px -3px gainsboro;
  transition: 0.3s ease;
  background: ${({ theme }) => theme["@primary"]};
  position: fixed;
  bottom: 60px;
  right: 60px;
  cursor: pointer;
  &:hover {
    box-shadow: 0px 8px 15px -3px gainsboro;
  }
  &:focus,
  &:active {
    outline: none;
  }
  @media print {
    display: none;
  }
`;

const Analytics = ({
  t,
  showTitle,
  analyticsCandidates,
  analyticsOpportunities,
  analyticsProfessions,
  analyticsAverageTime,
  headquarter,
  company,
  team,
  opportunity,
  exportCompanyCandidates,
  exportOpportunityCandidates,
  exportTeamCandidates,
  downloadingCandidates
}) => {
  useEffect(() => {
    if (opportunity)
      gtag.event({
        category: "VacancyMetrics",
        action: "VacancyMetrics",
        label: opportunity.id
      });
  });

  Chart.defaults.global.defaultFontColor = "hsl(240,2%,60%)";
  Chart.defaults.global.defaultFontFamily = "Poppins";
  const candidatesData = analyticsCandidates && {
    datasets: [
      {
        data: [
          analyticsCandidates.liked,
          analyticsCandidates.discarded,
          analyticsCandidates.rejected,
          analyticsCandidates.matches
        ],
        backgroundColor: ["#6DCDCC", "#FAE45A", "#EF7125", "#90DC9E"],
        label: t("analytics:charts.candidatesInfo"),
        fontFamily: "Poppins",
        fontWeight: "Light"
      }
    ],
    labels: [
      t("analytics:candidatesData.interested"),
      t("analytics:candidatesData.uninterested"),
      t("analytics:candidatesData.rejected"),
      t("analytics:candidatesData.matches")
    ]
  };

  const opportunitiesData = analyticsOpportunities && {
    labels: [
      t("analytics:opportunitiesData.published"),
      t("analytics:opportunitiesData.draft"),
      t("analytics:opportunitiesData.closed")
    ],
    datasets: [
      {
        label: t("analytics:charts.oppsQuantity"),
        backgroundColor: ["#6DCDCC", "#FAE45A", "#EF7125"],
        borderColor: ["#6DCDCC", "#FAE45A", "#EF7125"],
        borderWidth: 1,
        data: [
          analyticsOpportunities.published,
          analyticsOpportunities.open,
          analyticsOpportunities.closed
        ]
      }
    ]
  };

  const professionsData = analyticsProfessions && {
    labels: analyticsProfessions.map(item => item.name),
    datasets: [
      {
        label: t("analytics:charts.professionsQuantity"),
        backgroundColor: "#3D64FF82",
        borderColor: "#3D64FF",
        borderWidth: 1,
        hoverBackgroundColor: "#3D64FFd9",
        hoverBorderColor: "#3D64FF",
        data: analyticsProfessions.map(item => item.count)
      }
    ]
  };

  const getInfo = () => {
    if (opportunity && opportunity.id)
      return {
        kind: "opportunity",
        name: opportunity.title,
        id: opportunity.id,
        export: () => exportOpportunityCandidates(opportunity.id)
      };

    if (team && team.id)
      return {
        kind: "team",
        name: team.name,
        id: team.id,
        export: () => exportTeamCandidates(team.id)
      };

    if (company && company.id)
      return {
        kind: "company",
        name: company.name,
        id: company.id,
        export: () => exportCompanyCandidates(company.id)
      };

    if (headquarter && headquarter.id)
      return {
        kind: "headquarter",
        name: headquarter.name,
        id: headquarter.id,
        export: () => exportCompanyCandidates(headquarter.id)
      };
  };

  const info = getInfo();

  return (
    <AnalyticsContainer data-test="analytics-page">
      {showTitle && (
        <Page>
          <PageHeader>
            <AnalyticsTitle>
              {t("analytics:title.title", { name: info.name })}
            </AnalyticsTitle>
          </PageHeader>
        </Page>
      )}
      {analyticsCandidates && (
        <Page>
          <PageHeader>
            <AnalyticsSubtitle>
              {t("analytics:title.candidatesInfo")}
            </AnalyticsSubtitle>
            <Button
              icon="download"
              loading={downloadingCandidates}
              onClick={info.export}
            >
              {t("analytics:downloadCandidates")}
            </Button>
          </PageHeader>
          <Content>
            <CandidatesContainer data-test="candidates-analytics">
              <ChartContainer>
                <Doughnut
                  data={candidatesData}
                  options={{
                    legend: {
                      position: "bottom",
                      labels: {
                        fontFamily: "Poppins",
                        fontColor: "hsl(240,2%,60%)"
                      }
                    }
                  }}
                />
              </ChartContainer>
              <CountContainer style={{ maxWidth: "50%" }}>
                {analyticsCandidates && (
                  <>
                    <AnalyticsCard
                      dataTest="interested-candidates"
                      icon="like"
                      number={analyticsCandidates.liked}
                      duration={1}
                      delay={0}
                      color="#6DCDCC"
                      label={t("analytics:candidatesData.interested")}
                    />
                    <AnalyticsCard
                      icon="dislike"
                      dataTest="not-interested-candidates"
                      number={analyticsCandidates.discarded}
                      duration={1}
                      delay={0.3}
                      color="#FAE45A"
                      label={t("analytics:candidatesData.uninterested")}
                    />
                    <AnalyticsCard
                      icon="close-circle"
                      dataTest="rejected-candidates"
                      number={analyticsCandidates.rejected}
                      duration={1}
                      delay={0.6}
                      color="#EF7125"
                      label={t("analytics:candidatesData.rejected")}
                    />
                    <AnalyticsCard
                      icon="check-circle"
                      dataTest="matches-candidates"
                      number={analyticsCandidates.matches}
                      duration={1}
                      delay={0.9}
                      color="#90DC9E"
                      label={t("analytics:candidatesData.matches")}
                    />
                  </>
                )}
              </CountContainer>
            </CandidatesContainer>
          </Content>
        </Page>
      )}
      {analyticsProfessions && (
        <Page>
          <PageHeader>
            <AnalyticsSubtitle>
              {t("analytics:title.professionsInfo")}
            </AnalyticsSubtitle>
          </PageHeader>
          <Content>
            <ProfessionsContainer data-test="professions-analytics">
              <HorizontalBar
                data={professionsData}
                options={{
                  scales: {
                    xAxes: [
                      {
                        ticks: {
                          beginAtZero: true
                        }
                      }
                    ]
                  }
                }}
              />
              <ListContainer>
                {analyticsProfessions &&
                  analyticsProfessions.length > 0 &&
                  analyticsProfessions.map(item => (
                    <AnalyticsCard
                      key={item.profession}
                      dataTest={`profession-${item.profession}`}
                      listMode
                      icon="eye"
                      number={item.count}
                      duration={1}
                      delay={0}
                      label={item.name}
                    />
                  ))}
              </ListContainer>
            </ProfessionsContainer>
          </Content>
        </Page>
      )}
      {analyticsAverageTime && (
        <Page>
          <PageHeader>
            <AnalyticsSubtitle>
              {t("analytics:title.processInfo")}
            </AnalyticsSubtitle>
          </PageHeader>
          <Content>
            <AverageTimeContainer data-test="average-time-analytics">
              <GridContainer>
                <AnalyticsCard
                  dataTest="process-average"
                  icon="clock-circle"
                  duration={1}
                  hideCountUp
                  label={t("analytics:analyticsAverageTime.firstTolast")}
                  {...getNumberAndSuffix(
                    analyticsAverageTime.candidatesProcessAvg,
                    t
                  )}
                />

                <AnalyticsCard
                  dataTest="step-average"
                  icon="clock-circle"
                  duration={1}
                  hideCountUp
                  label={t("analytics:analyticsAverageTime.eachStep")}
                  {...getNumberAndSuffix(
                    analyticsAverageTime.candidatesStepAvg,
                    t
                  )}
                />
                <AnalyticsCard
                  dataTest="published-average"
                  icon="clock-circle"
                  number={analyticsAverageTime.publishedAvg}
                  duration={1}
                  hideCountUp
                  label={t(
                    "analytics:analyticsAverageTime.fromPublishedToClosed"
                  )}
                  {...getNumberAndSuffix(analyticsAverageTime.publishedAvg, t)}
                />
              </GridContainer>
            </AverageTimeContainer>
          </Content>
        </Page>
      )}
      {analyticsOpportunities && info.kind !== "opportunity" && (
        <Page>
          <PageHeader>
            <AnalyticsSubtitle>
              {t("analytics:title.opportunitiesInfo")}
            </AnalyticsSubtitle>
          </PageHeader>
          <Content>
            <OpportunitiesContainer data-test="opportunities-analytics">
              <ChartContainer>
                <Bar
                  data={opportunitiesData}
                  width={100}
                  height={50}
                  options={{
                    maintainAspectRatio: false,
                    legend: {
                      position: "bottom"
                    },
                    scales: {
                      yAxes: [
                        {
                          ticks: {
                            beginAtZero: true
                          }
                        }
                      ]
                    }
                  }}
                />
              </ChartContainer>
              <GridContainer>
                <AnalyticsCard
                  dataTest="published-opportunities"
                  icon="solution"
                  number={analyticsOpportunities.published}
                  duration={1}
                  label={t("analytics:opportunitiesData.published")}
                  color="#6DCDCC"
                />
                <AnalyticsCard
                  dataTest="draft-opportunities"
                  icon="solution"
                  number={analyticsOpportunities.open}
                  duration={1}
                  label={t("analytics:opportunitiesData.draft")}
                  color="#FAE45A"
                />
                <AnalyticsCard
                  dataTest="closed-opportunities"
                  icon="solution"
                  number={analyticsOpportunities.closed}
                  duration={1}
                  label={t("analytics:opportunitiesData.closed")}
                  color="#EF7125"
                />
              </GridContainer>
            </OpportunitiesContainer>
          </Content>
        </Page>
      )}
      <Tooltip placement="top" title={t("analytics:downloadPdf")}>
        <PrintButton onClick={() => window.print()}>
          <Icon type="file-pdf" />
        </PrintButton>
      </Tooltip>
    </AnalyticsContainer>
  );
};

const enhancer = compose(
  withRouter,
  withTranslation(["analytics"]),
  dispatchOnMount([
    ({ headquarter, company, team, opportunity }) =>
      fetchAnalyticsCandidatesOpps({ headquarter, company, team, opportunity }),
    ({ headquarter, company, team, opportunity }) =>
      fetchAnalyticsOpportunities({ headquarter, company, team, opportunity }),
    ({ headquarter, company, team, opportunity }) =>
      fetchAnalyticsAverageTime({ headquarter, company, team, opportunity }),
    ({ headquarter, company, team, opportunity }) =>
      fetchAnalyticsCandidatesProfessions({
        headquarter,
        company,
        team,
        opportunity
      })
  ]),
  connect(
    ({ analytics, headquarter, ...state }) => {
      return {
        headquarter,
        analyticsCandidates: analytics.candidates,
        analyticsProfessions: R.sort(
          R.descend(R.prop("count")),
          analytics.professions || []
        ),
        analyticsOpportunities: analytics.opportunities,
        analyticsAverageTime: analytics.averageTime,
        downloadingCandidates:
          isLoading(EXPORT_COMPANY_CANDIDATES, state) ||
          isLoading(EXPORT_OPPORTUNITY_CANDIDATES, state) ||
          isLoading(EXPORT_TEAM_CANDIDATES, state)
      };
    },
    {
      exportCompanyCandidates,
      exportOpportunityCandidates,
      exportTeamCandidates
    }
  ),
  spinWhile(
    FETCH_ANALYTICS_CANDIDATES_OPPS ||
      FETCH_ANALYTICS_OPPORTUNITIES ||
      FETCH_ANALYTICS_CANDIDATES_PROFESSIONS ||
      FETCH_ANALYTICS_AVERAGE_TIME,
    { initial: true },
    <Page>
      <Content>
        <SkeletonAnalytics />
      </Content>
    </Page>
  )
);

export default enhancer(Analytics);
